"use client";
import { Button } from "antd";
import React from "react";

interface EventType {
  title: string;
  description: string;
  startTime: string; // ISO string format
  endTime: string; // ISO string format
  location: string;
  attendees?: string[]; // Optional array for attendees
  url?: string; // Optional appointment URL
}

const formatDateTime = (iso: string) => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso.replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  }
  const pad = (value: number) => value.toString().padStart(2, "0");
  return [
    parsed.getUTCFullYear(),
    pad(parsed.getUTCMonth() + 1),
    pad(parsed.getUTCDate()),
    "T",
    pad(parsed.getUTCHours()),
    pad(parsed.getUTCMinutes()),
    pad(parsed.getUTCSeconds()),
    "Z",
  ].join("");
};

const escapeText = (value: string) =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const createUid = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return `${crypto.randomUUID()}@openings.link`;
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}@openings.link`;
};

const buildIcsContent = (event: EventType) => {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Openings//Calendar//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${createUid()}`,
    `DTSTAMP:${formatDateTime(new Date().toISOString())}`,
    `DTSTART:${formatDateTime(event.startTime)}`,
    `DTEND:${formatDateTime(event.endTime)}`,
    `SUMMARY:${escapeText(event.title)}`,
  ];

  if (event.description) {
    lines.push(`DESCRIPTION:${escapeText(event.description)}`);
  }

  if (event.location) {
    lines.push(`LOCATION:${escapeText(event.location)}`);
  }

  if (event.url) {
    lines.push(`URL:${escapeText(event.url)}`);
  }

  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n");
};

const isIOSSafari = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  const iOS = /iPad|iPhone/.test(ua);
  const webkit = /WebKit/i.test(ua);
  return iOS && webkit && !/CriOS/i.test(ua);
};

const isIOSChrome = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  const iOS = /iPad|iPhone/.test(ua);
  return iOS && /CriOS/i.test(ua);
};

const formatForGoogle = (iso: string) => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso.replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  }
  return parsed
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}Z$/, "Z");
};

const ICalendar = ({ event }: { event: EventType }) => {
  const normalizedEvent = React.useMemo(
    () => ({
      ...event,
      endTime: event.endTime ?? event.startTime,
      description: event.description ?? "",
      location: event.location ?? "",
    }),
    [event],
  );

  const handleDownload = React.useCallback(() => {
    const icsContent = buildIcsContent(normalizedEvent);
    const dataUrl = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;

    if (isIOSChrome() || isIOSSafari()) {
      window.open(dataUrl, "_blank");
      return;
    }

    const blob = new Blob([icsContent], {
      type: "text/calendar;charset=utf-8",
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "openings-appointment.ics";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }, [normalizedEvent]);

  const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
    normalizedEvent.title,
  )}&details=${encodeURIComponent(
    normalizedEvent.description,
  )}&dates=${formatForGoogle(normalizedEvent.startTime)}/${formatForGoogle(
    normalizedEvent.endTime,
  )}&location=${encodeURIComponent(
    normalizedEvent.location,
  )}&sf=true&output=xml`;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Button type="default" onClick={handleDownload}>
        Apple Calendar
      </Button>
      <div
        style={{
          width: 1,
          height: "1em",
          background:
            "var(--openings-booking-skin-divider, #d9d9d9)",
        }}
      />
      <Button
        type="default"
        href={googleCalendarUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        Google Calendar
      </Button>
    </div>
  );
};

export default ICalendar;
